import { runInAction, action, computed, makeObservable } from 'mobx'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { NotificationsAPIRoot } from './API'
import { PrimeMember } from 'iam/MemberStore'
import Notification from './models/Notification'
import {
  NotificationDTO,
  FindNotificationsQuery,
  SetNotificationStatusPayload,
  NotificationStatus,
} from './dtos/notifications'
import { collection } from 'infra/collection'
import { AxiosError } from 'axios'
import { Id } from 'types/definitions'
import Workspace from 'iam/models/Workspace'
import { share, filter, map } from 'rxjs/operators'
import { Observable } from 'rxjs'

export default class NotificationStore {
  private notificationsScrollProviders = new Map()

  notifications = collection<NotificationDTO, Notification>({
    create: () => new Notification(this.primeMember),
  })

  newNotifications$: Observable<Notification>

  constructor(
    private api: NotificationsAPIRoot,
    private primeMember: PrimeMember,
  ) {
    makeObservable(this)
    const notificationUpdates$ = this.api.notifications
      .when<{
        type: 'NotificationUpdated'
        data: NotificationDTO
      }>(`NotificationUpdated`)
      .pipe(share())

    notificationUpdates$.subscribe((e) => this.notifications.set(e.data))

    this.newNotifications$ = notificationUpdates$.pipe(
      filter((e) => e.data.status === 'Sent'),
      map((e) => this.notifications.set(e.data)),
    )
  }

  @computed
  get unreadCount() {
    return this.notifications.filter((n) => n.status !== 'Read').length
  }

  getScrollProviderForWorkspace = (workspaceId: string) => {
    if (!this.notificationsScrollProviders.has(workspaceId)) {
      this.notificationsScrollProviders.set(
        workspaceId,
        new InfiniteScrollProvider({
          memoizeInitial: true,
          limit: 100,
          fetchItems: (params) => {
            return this.fetch({
              ...params,
              workspace_id: workspaceId,
            })
          },
        }),
      )

      this.notificationsScrollProviders.get(workspaceId).fetch()
    }
    return this.notificationsScrollProviders.get(workspaceId)
  }

  fetch = (params: FindNotificationsQuery) => {
    return this.api.notifications.find(params).then((result) => {
      const data = result.data
      return {
        ...result,
        data: this.notifications.set(data),
      }
    })
  }

  markAsRead = async (notification: Notification) => {
    if (notification.status === 'Read') {
      return
    }

    const prev = notification.status
    notification.set({ status: 'Read' })
    await this.changeStatus({
      id: Id.assert(notification.id),
      status: 'Read',
    }).catch((err: AxiosError) => {
      notification.set({ status: prev })
      throw err
    })
  }

  changeStatus = async ({ id, status }: SetNotificationStatusPayload) => {
    const params = { id, status }
    return this.api.notifications
      .changeStatus(params)
      .then(this.notifications.set)
  }

  /**
   * Marks all notifications in the specified workspace as read.
   * if `Workspace.none` is passed in, marks every notification in every workspace read.
   * That is, notifications that have been loaded so far.
   *
   * @param workspace
   */
  markAllAsRead = async (workspace: Workspace) => {
    const toMark = this.notifications.filter(
      (x) =>
        x.status !== 'Read' &&
        (workspace === Workspace.none || x.recipient.workspace === workspace),
    )
    const prev: Array<[Id, NotificationStatus]> = toMark.map((n) => [
      n.id,
      n.status,
    ])
    runInAction(() => {
      toMark.forEach((n) => {
        n.set({ status: 'Read' })
      })
    })
    return this.api.notifications
      .changeStatusBulk(
        toMark.map((x) => ({ id: Id.assert(x.id), status: 'Read' })),
      )
      .then((data) => this.notifications.set(data))
      .catch(
        // Restores status if it failed.
        action((err: AxiosError) => {
          prev.forEach(([id, status]) => {
            if (this.notifications.get(Id.assert(id))) {
              this.notifications.get(Id.assert(id))!.set({ status })
            }
          })
          throw err
        }),
      )
  }
}
